import React, { useState, useEffect } from 'react';
import { useUser } from '../utils/hooks/useUser';
import { fetchAuthSession } from '@aws-amplify/auth';

const SubscriptionStatus = () => {
  const { userEmail, userId } = useUser();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [quotaData, setQuotaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userEmail && userId) {
      fetchSubscriptionData();
    }
  }, [userEmail, userId]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      // Fetch subscription status
      const subscriptionResponse = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/check-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,POST'
        },
        body: JSON.stringify({
          user_id: userId,
          email: userEmail
        })
      });

      if (!subscriptionResponse.ok) {
        throw new Error('Failed to fetch subscription data');
      }

      const result = await subscriptionResponse.json();
      setSubscriptionData(result);
      setQuotaData(result.quota);

    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'text-green-600' : 'text-red-600';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString * 1000).toLocaleDateString();
  };

  const calculateUsagePercentage = (used, total) => {
    if (!total || total === 0) return 0;
    return Math.round((used / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchSubscriptionData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Status</h1>
          <p className="text-gray-600">Manage your subscription and view usage details</p>
        </div>

        {/* Subscription Status Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className={`text-lg font-semibold ${getStatusColor(subscriptionData?.isActive)}`}>
                {getStatusText(subscriptionData?.isActive)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Plan</p>
              <p className="text-lg font-semibold text-gray-900">
                {subscriptionData?.subscriptionType || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Start Date</p>
              <p className="text-lg text-gray-900">
                {formatDate(subscriptionData?.startDate)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">End Date</p>
              <p className="text-lg text-gray-900">
                {formatDate(subscriptionData?.endDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Quota Usage Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Quota</h2>
          {quotaData ? (
            <div className="space-y-6">
              {/* Resume Optimization Quota */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium text-gray-900">Resume Optimization</h3>
                  <span className="text-sm text-gray-500">
                    {quotaData.resume_optimization_used || 0} / {quotaData.resume_optimization_total || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${calculateUsagePercentage(
                        quotaData.resume_optimization_used || 0,
                        quotaData.resume_optimization_total || 0
                      )}%`
                    }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {calculateUsagePercentage(
                    quotaData.resume_optimization_used || 0,
                    quotaData.resume_optimization_total || 0
                  )}% used
                </p>
              </div>

              {/* Mock Interview Quota */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium text-gray-900">Mock Interviews</h3>
                  <span className="text-sm text-gray-500">
                    {quotaData.mock_interview_used || 0} / {quotaData.mock_interview_total || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${calculateUsagePercentage(
                        quotaData.mock_interview_used || 0,
                        quotaData.mock_interview_total || 0
                      )}%`
                    }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {calculateUsagePercentage(
                    quotaData.mock_interview_used || 0,
                    quotaData.mock_interview_total || 0
                  )}% used
                </p>
              </div>


            </div>
          ) : (
            <p className="text-gray-500">No quota data available</p>
          )}
        </div>


      </div>
    </div>
  );
};

export default SubscriptionStatus; 